import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environment';
import { forkJoin, ReplaySubject, timer, Observable, of } from 'rxjs';
import { filter, take, takeUntil, switchMap, map } from 'rxjs/operators';
import { ROLES_KEYS, PermissionEnum, Permission, InAppNotificationType, User } from '@interfaces';
import { SnackbarService } from '@shared/snack-bar/snack-bar.service';
import { RouterFacade } from '@state/router';
import { UserAuthFacade } from '@state/user-auth';
import { DeviceInfoService } from '@core/services/device-info.service';
import { ActivatedRoute } from '@angular/router';
import { UserProcessFacade } from '@state/user-process';
import { roleToPath } from '@constants';
import { InAppNotificationsFacade } from '@state/notifications';
import { ChatFacade } from '@state/chat';
import { ManagerFacade } from '@state/manager';

@Component({
  selector: 'app-init-user',
  templateUrl: './init-user.component.html',
  styleUrls: ['./init-user.component.scss'],
})
export class InitUserComponent implements OnInit, OnDestroy {
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  private redirect: string | null = null;

  public value = 0;
  public spinner = true;
  public progressBar = false;
  public animationTime = environment.authAnimationTime;

  public currentMessage = '';
  public messages = [
    this.translateService.instant('LOADING_ANIMATION_PROGRESS_BAR.TEXT1'),
    this.translateService.instant('LOADING_ANIMATION_PROGRESS_BAR.TEXT2'),
    this.translateService.instant('LOADING_ANIMATION_PROGRESS_BAR.TEXT3'),
    this.translateService.instant('LOADING_ANIMATION_PROGRESS_BAR.TEXT4'),
  ];

  constructor(
    private readonly userAuthFacade: UserAuthFacade,
    private readonly translateService: TranslateService,
    private readonly snackService: SnackbarService,
    private readonly routerFacade: RouterFacade,
    private readonly deviceInfoService: DeviceInfoService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userProcessFacade: UserProcessFacade,
    private readonly inAppNotifications: InAppNotificationsFacade,
    private readonly chatFacade: ChatFacade,
    private readonly managerFacade: ManagerFacade
  ) {}

  public ngOnInit(): void {
    this.showProgressBar(1000);

    setTimeout(() => {
      this.changeMessage(this.animationTime * 1000 - 1000);
    }, 1000);

    const minDelay$ = timer(this.animationTime * 1000);

    const myselfLoaded$ = this.userAuthFacade.getMyselfSuccess$.pipe(
      filter(({ user }) => {
        return user?.role?.key && user?.role?.permissions?.length > 0;
      }),
      take(1),
      takeUntil(this.destroy$)
    );

    const dataByRoleLoaded$ = myselfLoaded$.pipe(
      switchMap(({ user }) => this.loadRequiredDataByRole(user)),
      take(1)
    );

    forkJoin({
      myself: myselfLoaded$,
      dataByRole: dataByRoleLoaded$,
      delay: minDelay$,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ myself: { user } }) => {
        this.handleNavigationAfterGetData(user);
      });

    const { redirect } = this.activatedRoute.snapshot.queryParams;
    this.userAuthFacade.getMyself(redirect);
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  /**
   * Loads additional data required for the given user role.
   *
   * Flow:
   * 1. Determine the user's role.
   * 2. For specific roles, trigger required requests (e.g., foreigner processes).
   * 3. Wait for corresponding success actions (NgRx) before completing.
   *
   * Returns:
   * Observable<boolean> – emits true when all required data is loaded.
   */
  private loadRequiredDataByRole(user: Partial<User>): Observable<any> {
    const roleKey = user.role.key;
    const requests: Observable<any>[] = [];

    // FOR ALL
    this.userAuthFacade.getBranding$().pipe(take(1), takeUntil(this.destroy$)).subscribe();
    this.inAppNotifications.getMaintenanceInfo$().pipe(take(1), takeUntil(this.destroy$)).subscribe(); // NO WAIT

    // FOR SPECIFIC ROLE
    if (roleKey === ROLES_KEYS.Foreigner) {
      requests.push(this.inAppNotifications.getUnreadCountOfTypeSuccess$.pipe(take(1), takeUntil(this.destroy$)));
      requests.push(this.userProcessFacade.getMyProcessesSuccess$.pipe(take(1), takeUntil(this.destroy$)));
      requests.push(this.chatFacade.getMyChatSuccess$.pipe(take(1), takeUntil(this.destroy$)));

      this.inAppNotifications.getUnreadCountOfType(InAppNotificationType.EMPLOYEE_SENT_MESSAGE_IN_CHAT, null);
      this.userProcessFacade.getMyProcesses();
      this.chatFacade.getMyChat();
    }

    if (roleKey === ROLES_KEYS.Employer) {
      requests.push(this.managerFacade.getMyManagedCompanies$().pipe(take(1), takeUntil(this.destroy$)));
    }

    if (requests.length === 0) {
      return of(true);
    }

    return forkJoin(requests).pipe(map(() => true));
  }

  private handleNavigationAfterGetData(user: Partial<User>): void {
    const roleKey = user.role.key as ROLES_KEYS;

    let linkParams = [roleToPath[roleKey]];
    const queryParams = {} as any;

    if (this.redirect?.length >= 4) {
      const [path, queryParamsString] = this.redirect.split('?');
      linkParams = path.split('/').filter(pathPart => !!pathPart);

      if (queryParamsString?.length) {
        queryParamsString.split('&').forEach(keyVal => {
          const [key, val] = keyVal.split('=');
          queryParams[key] = val;
        });
      }
    }

    const usingMobileDevice = this.deviceInfoService.getDeviceType() !== 'DESKTOP';
    const isAllowedToUseMobileDevice = user.role.permissions.find(
      (permission: Permission) => permission.key === PermissionEnum.CAN_USE_MOBILE
    );

    if (usingMobileDevice && !isAllowedToUseMobileDevice) {
      this.snackService.showError('COMMON.ERROR.NOT_AUTHORIZED_TO_USE_MOBILE_DEVICE');
      this.userAuthFacade.logout({ wasUnauthorized: false, sessionTimedOutLogout: false });
      return;
    }

    this.routerFacade.changeRoute({
      linkParams,
      extras: { queryParams, replaceUrl: true },
    });
  }

  // PROGRESS BAR
  public showProgressBar(time: number): void {
    setTimeout(() => {
      this.spinner = false;
      this.progressBar = true;
    }, time);
  }

  public changeMessage(animationTime: number): void {
    const timePerMessage = animationTime / this.messages.length;
    const percentPerMessage = 100 / this.messages.length;

    this.currentMessage = this.messages[0];
    for (let i = 0; i < this.messages.length; i++) {
      setTimeout(() => {
        this.currentMessage = this.messages[i];
        this.value = percentPerMessage * (i + 1);
      }, timePerMessage * i);
    }
  }
}
